import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { BarChart3, CheckCircle2, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const pct = (r) => (r && r.value != null ? `${(r.value * 100).toFixed(1)} %` : "—");
const frac = (r) => (r ? `${r.num} / ${r.den}` : "");

// Statuts réservés : toujours icône + libellé, jamais la couleur seule
function StatusChip({ ok, okLabel, koLabel }) {
  if (ok == null) return null;
  return ok ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
      <CheckCircle2 className="w-3 h-3" /> {okLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700">
      <AlertTriangle className="w-3 h-3" /> {koLabel}
    </span>
  );
}

function Tile({ label, value, sub, status }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">{label}</div>
      <div className="font-display font-extrabold text-2xl text-slate-900 leading-none">{value ?? "—"}</div>
      {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
      {status && <div className="mt-1.5">{status}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h2 className="font-display font-bold text-sm text-slate-900 uppercase tracking-wide mb-2">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

export default function AdminKpis() {
  const [k, setK] = useState(null);
  const [recon, setRecon] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [kpis, reports] = await Promise.all([
        api.get("/admin/kpis"),
        api.get("/admin/reconciliation?limit=1"),
      ]);
      setK(kpis.data);
      setRecon(reports.data[0] || null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runReconcile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/jobs/reconcile_ledger/run");
      toast.success(`Réconciliation : ${data.result?.anomalies ?? 0} anomalie(s) sur ${data.result?.checked?.missions ?? 0} missions`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-kpis-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-5xl">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-orange-600" />
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Administration</div>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight mb-1">Indicateurs de succès</h1>
          <p className="text-sm text-slate-500 mb-6">
            Les 11 indicateurs du PRD v2.0 — chaque ratio est audité (numérateur / dénominateur).
          </p>

          {!k ? (
            <div className="py-16 text-center text-slate-400">Chargement…</div>
          ) : (
            <>
              <Section title="Économie">
                <Tile
                  label="Take rate réalisé"
                  value={pct(k.take_rate)}
                  sub={`${k.take_rate.commissions_eur.toFixed(2)} € de commissions / ${k.take_rate.gmv_eur.toFixed(2)} € encaissés (missions clôturées)`}
                  status={k.take_rate.value != null && (
                    <StatusChip ok={k.take_rate.value >= 0.14} okLabel="Cible ≈ 15 % tenue" koLabel="Sous la cible 15 %" />
                  )}
                />
                <Tile
                  label="Part du plancher 25 €"
                  value={pct(k.take_rate.floor_share)}
                  sub={`${frac(k.take_rate.floor_share)} paiements au plancher (bas du panier)`}
                />
                <Tile
                  label="Double appariement"
                  value={pct(k.double_matching)}
                  sub={`${frac(k.double_matching)} mouvements avec mission retour monétisée`}
                />
              </Section>

              <Section title="Paiement">
                <Tile
                  label="Conversion au paiement"
                  value={pct(k.payment_conversion)}
                  sub={`${frac(k.payment_conversion)} offres acceptées suivies d'un acompte capturé`}
                />
                <Tile
                  label="Échec de débit à J-3"
                  value={pct(k.balance_capture.failure_rate)}
                  sub={`${frac(k.balance_capture.failure_rate)} captures différées en échec au moins une fois`}
                />
                <Tile
                  label="Récupération après relance"
                  value={pct(k.balance_capture.recovery_rate)}
                  sub={frac(k.balance_capture.recovery_rate)}
                />
              </Section>

              <Section title="Exécution & litiges">
                <Tile
                  label="Photos arrivée → libération"
                  value={k.release_delay_hours.avg != null ? `${k.release_delay_hours.avg} h` : "—"}
                  sub={`Cible ≤ ${k.release_delay_hours.target_max} h · échantillon ${k.release_delay_hours.sample}`}
                  status={k.release_delay_hours.avg != null && (
                    <StatusChip ok={k.release_delay_hours.avg <= k.release_delay_hours.target_max}
                                okLabel="Dans la cible" koLabel="Cible dépassée" />
                  )}
                />
                <Tile
                  label="Contestation client"
                  value={pct(k.contest_rate)}
                  sub={`${frac(k.contest_rate)} livraisons contestées dans la fenêtre 72 h`}
                />
                <Tile
                  label="Litiges"
                  value={k.disputes.avg_resolution_hours != null ? `${k.disputes.avg_resolution_hours} h` : "—"}
                  sub={`Délai moyen de résolution · ${k.disputes.open} ouvert(s) · escalade ${pct(k.disputes.escalation_rate)}`}
                />
              </Section>

              <Section title="Annulations">
                <Tile
                  label="Annulation tardive client"
                  value={pct(k.client_cancellations.late_rate)}
                  sub={`${frac(k.client_cancellations.late_rate)} annulations < 72 h · ${k.client_cancellations.kept_eur.toFixed(2)} € conservés (${pct(k.client_cancellations.kept_share)})`}
                />
                <Tile
                  label="Annulation transporteur"
                  value={pct(k.transporter_cancellations.rate)}
                  sub={frac(k.transporter_cancellations.rate)}
                />
                <Tile
                  label="Succès du re-matching"
                  value={pct(k.transporter_cancellations.rematch_success)}
                  sub={`${frac(k.transporter_cancellations.rematch_success)} republications prioritaires re-servies`}
                />
              </Section>

              <Section title="Risque & conformité">
                <Tile
                  label="Chargebacks Stripe"
                  value={pct(k.chargeback_rate)}
                  sub={frac(k.chargeback_rate)}
                />
                <Tile
                  label="Conformité documentaire"
                  value={pct(k.document_compliance)}
                  sub={`${frac(k.document_compliance)} dépanneurs avec 100 % des documents à jour`}
                />
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Réconciliation ledger</div>
                  {recon ? (
                    <>
                      <div className="font-display font-extrabold text-2xl text-slate-900 leading-none">
                        {recon.anomaly_count}
                      </div>
                      <div className="text-xs text-slate-500 mt-1.5">
                        anomalie(s) au dernier rapprochement ({new Date(recon.ran_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })},
                        {" "}{recon.checked?.missions} missions) · {recon.stripe?.mode}
                      </div>
                      <div className="mt-1.5">
                        <StatusChip ok={recon.anomaly_count === 0} okLabel="Ledger cohérent" koLabel="Écarts à examiner" />
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-400 mt-1">Aucun rapport pour l'instant.</div>
                  )}
                  <Button size="sm" onClick={runReconcile} disabled={busy} variant="outline" className="mt-3 h-8">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    Lancer maintenant
                  </Button>
                </div>
              </Section>

              <div className="text-[11px] text-slate-400">Généré le {new Date(k.generated_at).toLocaleString("fr-FR")}</div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
